/* eslint-disable no-magic-numbers */
import { UsePointToProjectionResult } from '@/utils/map/usePointToProjection';
import { Style, Text } from 'ol/style';
import Icon from 'ol/style/Icon';
import { MapInstance } from '@/types/map';
import Polygon from 'ol/geom/Polygon';
import { Coordinate } from 'ol/coordinate';
import { FEATURE_TYPE } from '@/constants/features';
import {
  DEFAULT_FONT_NAME_ENUM,
  DEFAULT_FONT_STYLE_ENUM,
  DEFAULT_FONT_WEIGHT_ENUM,
  MAP_ELEMENT_TYPES,
  WHITE_COLOR,
  TRANSPARENT_COLOR,
} from '@/components/Map/MapViewer/MapViewer.constants';
import { MapSize } from '@/redux/map/map.types';
import getStyle from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getStyle';
import getFill from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getFill';
import getBoundingBoxPolygon from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/utils/getBoundingBoxPolygon';
import { LayerImageAdapter } from '@/utils/layer/LayerImageAdapter';
import BaseMultiPolygon from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/BaseMultiPolygon';
import VectorSource from 'ol/source/Vector';
import { OverlayBioEntityRender } from '@/types/OLrendering';
import { OverlayOrder } from '@/redux/overlayBioEntity/overlayBioEntity.utils';
import { getPolygonLatitudeCoordinates } from '@/components/Map/MapViewer/utils/config/overlaysLayer/getPolygonLatitudeCoordinates';
import { ZERO } from '@/constants/common';
import { getDataOverlays } from '@/services/pluginsManager/map/overlays/getDataOverlays';
import getStroke from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getStroke';
import { GetOverlayBioEntityColorByAvailableProperties } from '@/components/Map/MapViewer/utils/config/overlaysLayer/useGetOverlayColor';
import { Point } from 'ol/geom';
import getScaledElementStyle from '@/components/Map/MapViewer/utils/mapElementsRendering/style/getScaledElementStyle';
import { getFontName } from '@/components/Map/MapViewer/utils/mapElementsRendering/text/getFontName';
import { getLoupeRenderView } from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/utils/loupeRenderView';
import { getGlyphImage } from '@/utils/glyph/glyphImageCache';

export type GlyphProps = {
  elementId: number;
  glyphId: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  allowUiInteraction: boolean;
  pointToProjection: UsePointToProjectionResult;
  mapInstance: MapInstance;
  mapSize: MapSize;
  vectorSource?: VectorSource;
  overlays?: Array<OverlayBioEntityRender>;
  overlaysOrder?: Array<OverlayOrder>;
  overlaysVisible: boolean;
  overlayNotMatchingAlpha?: number | null;

  getOverlayColor?: GetOverlayBioEntityColorByAvailableProperties;
};

export default class Glyph extends BaseMultiPolygon {
  style: Style = new Style();

  noGlyphStyle: Style = new Style();

  imageScale: number = 1;

  polygonStyle: Style = new Style();

  elementId: number;

  width: number;

  height: number;

  x: number;

  y: number;

  zIndex: number;

  glyphId: number | null;

  widthOnMap: number;

  heightOnMap: number;

  pixelRatio: number = 1;

  minResolution: number;

  imageWidth: number = 1;

  imageHeight: number = 1;

  imageWidthOnMap: number = 1;

  imageHeightOnMap: number = 1;

  mapInstance: MapInstance;

  pointToProjection: UsePointToProjectionResult;

  mapSize: MapSize;

  overlays: Array<OverlayBioEntityRender> = [];

  overlaysOrder: Array<OverlayOrder> = [];

  getOverlayColor: GetOverlayBioEntityColorByAvailableProperties | null;

  constructor({
    elementId,
    glyphId,
    x,
    y,
    width,
    height,
    zIndex,
    allowUiInteraction,
    pointToProjection,
    mapInstance,
    mapSize,
    vectorSource,
    overlaysVisible,
    overlayNotMatchingAlpha,
    getOverlayColor,
    overlays,
    overlaysOrder,
  }: GlyphProps) {
    super({
      type: FEATURE_TYPE.GLYPH,
      sboTerm: '',
      id: elementId,
      complexId: null,
      compartmentId: null,
      pathwayId: null,
      x,
      y,
      width,
      height,
      zIndex,
      allowUiInteraction,
      text: '',
      fontSize: 12,
      fontName: DEFAULT_FONT_NAME_ENUM,
      fontWeight: DEFAULT_FONT_WEIGHT_ENUM,
      fontStyle: DEFAULT_FONT_STYLE_ENUM,
      nameX: x,
      nameY: y,
      nameWidth: width,
      nameHeight: height,
      fontColor: WHITE_COLOR,
      nameVerticalAlign: 'MIDDLE',
      nameHorizontalAlign: 'CENTER',
      fillColor: WHITE_COLOR,
      borderColor: WHITE_COLOR,
      pointToProjection,
      vectorSource: vectorSource || null,
      overlaysVisible,
      overlayNotMatchingAlpha: overlayNotMatchingAlpha || null,
      backgroundId: -1,
      mapSize,
      mapInstance,
    });

    this.elementId = elementId;
    this.width = width;
    this.height = height;
    this.mapSize = mapSize;
    this.glyphId = glyphId;
    this.x = x;
    this.y = y;
    this.zIndex = zIndex;
    this.mapInstance = mapInstance;
    this.pointToProjection = pointToProjection;
    this.overlays = overlays || [];
    this.overlaysOrder = overlaysOrder || [];
    this.overlayNotMatchingAlpha = overlayNotMatchingAlpha || null;
    this.getOverlayColor = getOverlayColor || null;
    const point1 = this.pointToProjection({ x: 0, y: 0 });
    const point2 = this.pointToProjection({ x: this.width, y: this.height });
    this.widthOnMap = Math.abs(point2[0] - point1[0]);
    this.heightOnMap = Math.abs(point2[1] - point1[1]);

    const maxZoom = mapInstance?.getView().get('originalMaxZoom');
    this.minResolution = mapInstance?.getView().getResolutionForZoom(maxZoom) || 1;
    this.pixelRatio = this.widthOnMap / this.minResolution / this.width;

    const polygon = getBoundingBoxPolygon({
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      pointToProjection: this.pointToProjection,
    });

    this.polygons.push(polygon);

    this.setStyles();

    this.createPolygons();
    this.drawMultiPolygonFeature(mapInstance);
    this.drawImage();
  }

  private setStyles(): void {
    this.polygonStyle = getStyle({
      geometry: this.polygons[0],
      zIndex: this.zIndex,
      borderColor: TRANSPARENT_COLOR,
      fillColor: TRANSPARENT_COLOR,
    });

    const font = getFontName(this.fontName);

    this.noGlyphStyle = getStyle({
      geometry: this.polygons[0],
      zIndex: this.zIndex,
      fillColor: '#E7E7E7',
    });
    this.noGlyphStyle.setText(
      new Text({
        text: 'No image',
        font: `12pt ${font}`,
        fill: getFill({ color: '#000' }),
        overflow: true,
      }),
    );
  }

  private refreshPolygon(): void {
    const polygon = getBoundingBoxPolygon({
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      pointToProjection: this.pointToProjection,
    });
    this.polygons.length = 0;
    this.polygons.push(polygon);
    this.polygonStyle.setGeometry(polygon);
    this.drawImage();
  }

  protected updateElement(imageObject: LayerImageAdapter): void {
    this.elementId = imageObject.getOriginalId();
    this.x = imageObject.x;
    this.y = imageObject.y;
    this.zIndex = imageObject.z;
    this.width = imageObject.width;
    this.height = imageObject.height;
    this.glyphId = imageObject.glyph;

    this.refreshPolygon();
    this.setStyles();
    this.drawImage();
    this.feature.changed();
  }

  protected setImageScaleAndDimensions(height: number, width: number): void {
    this.widthOnMap = width;
    this.heightOnMap = height;
    const heightScale = height / this.imageHeightOnMap;
    const widthScale = width / this.imageWidthOnMap;
    if (heightScale < widthScale) {
      this.imageScale = heightScale;
      this.widthOnMap = (this.heightOnMap * this.imageWidth) / this.imageHeight;
    } else {
      this.imageScale = widthScale;
      this.heightOnMap = (this.widthOnMap * this.imageHeight) / this.imageWidth;
    }
  }

  private setCoordinates(coords: Coordinate[][]): void {
    const geometry = this.polygonStyle.getGeometry();
    if (geometry && geometry instanceof Polygon) {
      geometry.setCoordinates(coords);
    }
  }

  private drawImage(): void {
    const { glyphId } = this;
    if (!glyphId) {
      return;
    }

    getGlyphImage(glyphId)
      .then(img => {
        // the glyph may have been replaced while the image was being loaded
        if (this.glyphId !== glyphId) {
          return;
        }
        this.applyImage(img);
      })
      .catch(() => {
        // the element keeps its 'No image' style when the image cannot be loaded
      });
  }

  private applyImage(img: HTMLImageElement): void {
    this.imageWidth = img.naturalWidth;
    this.imageHeight = img.naturalHeight;
    const imagePoint1 = this.pointToProjection({ x: 0, y: 0 });
    const imagePoint2 = this.pointToProjection({ x: this.imageWidth, y: this.imageHeight });
    this.imageWidthOnMap = Math.abs(imagePoint2[0] - imagePoint1[0]);
    this.imageHeightOnMap = Math.abs(imagePoint2[1] - imagePoint1[1]);
    this.setImageScaleAndDimensions(this.heightOnMap, this.widthOnMap);
    this.style = new Style({
      image: new Icon({
        anchor: [0, 0],
        img,
        size: [this.imageWidth, this.imageHeight],
      }),
      zIndex: this.zIndex,
    });
    this.feature.changed();
  }

  protected processElementStyles(scale: number): Array<Style> {
    const getAnchorAndCoords = this.feature.get('getAnchorAndCoords');
    let anchor = [0, 0];
    let coords = [this.x, this.y];

    const geometry = this.feature.getGeometry();
    if (geometry) {
      const polygonExtent = geometry.getExtent();
      if (polygonExtent) {
        coords = [polygonExtent[0], polygonExtent[3]];
        const width = Math.abs(polygonExtent[0] - polygonExtent[2]);
        const height = Math.abs(polygonExtent[1] - polygonExtent[3]);
        this.setImageScaleAndDimensions(height, width);
      }
    } else {
      return [];
    }
    if (getAnchorAndCoords instanceof Function) {
      const anchorAndCoords = getAnchorAndCoords(coords);
      anchor = anchorAndCoords.anchor;
      coords = anchorAndCoords.coords;
    }

    if (this.style.getImage()) {
      this.style.getImage()?.setScale(scale * this.pixelRatio * this.imageScale);
      (this.style.getImage() as Icon).setAnchor(anchor);
      this.style.setGeometry(new Point(coords));
      return [this.style, this.polygonStyle];
    }
    return [getScaledElementStyle(this.noGlyphStyle, undefined, scale)];
  }

  protected createPolygons(): void {
    this.drawOverlays();
  }

  drawOverlays(): void {
    this.overlays.forEach(entity => {
      if (entity.value === Infinity || !this.getOverlayColor) {
        return;
      }
      const { xMin, xMax } = getPolygonLatitudeCoordinates({
        width: entity.width,
        nOverlays: this.overlaysOrder.length,
        xMin: entity.x1,
        overlayIndexBasedOnOrder:
          this.overlaysOrder.find(({ id }) => id === entity.overlayId)?.index || ZERO,
      });
      const color = this.getOverlayColor(entity);
      const polygon = new Polygon([
        [
          this.pointToProjection({ x: xMin, y: entity.y1 }),
          this.pointToProjection({ x: xMax, y: entity.y1 }),
          this.pointToProjection({ x: xMax, y: entity.y2 }),
          this.pointToProjection({ x: xMin, y: entity.y2 }),
        ],
      ]);
      if (entity.sourceData) {
        const overlay = getDataOverlays().filter(
          filteredOverlay => filteredOverlay.id === entity.overlayId,
        )[0];
        polygon.set('tooltip', `[${overlay?.name}] Value in source data is '${entity.sourceData}'`);
      }
      polygon.set('type', MAP_ELEMENT_TYPES.OVERLAY);
      const style = getStyle({
        geometry: polygon,
        borderColor: color,
        fillColor: color,
        zIndex: this.zIndex + 1,
      });
      polygon.set(
        'strokeStyle',
        getStroke({
          color,
        }),
      );
      this.overlaysPolygons.push(polygon);
      this.overlaysStyles.push(style);
    });
  }

  protected drawMultiPolygonFeature(mapInstance: MapInstance): void {
    super.drawMultiPolygonFeature(mapInstance);
    this.feature.set('setCoordinates', this.setCoordinates.bind(this));
    this.feature.set('refreshPolygon', this.refreshPolygon.bind(this));
    this.feature.set('updateElement', this.updateElement.bind(this));
    this.feature.set(
      'getAnchorAndCoords',
      (coords: Coordinate): { anchor: Array<number>; coords: Coordinate } => {
        // Anchor to the rendering map's view center: the loupe's while it renders
        // (published via loupeRenderView), the primary map's otherwise. Using the
        // primary center in the loupe pins the icon point outside the loupe
        // viewport, so OpenLayers culls it once the cursor moves away.
        const center = (getLoupeRenderView() ?? this.mapInstance?.getView())?.getCenter();
        let anchorX = 0;
        let anchorY = 0;
        if (center) {
          anchorX = (center[0] - coords[0]) / this.widthOnMap;
          anchorY = -(center[1] - coords[1]) / this.heightOnMap;
        }
        return { anchor: [anchorX, anchorY], coords: center || [0, 0] };
      },
    );
  }
}
